import React from 'react';
import type { TreeItem } from '@ditioas/web-ui';
import { TreeViewFolderIcon } from '@ditioas/web-ui';
import type { TFunction } from 'i18next';

import type { FolderOrItem } from 'components/FolderStructure/types';
import OptionDisplay from 'components/OptionDisplay/OptionDisplay';
import type { ProjectTask } from 'model/Projects/types';
import { sortTree } from 'model/ResourcePlanning/utils';
import type { UserSettingsTask } from 'model/UserProjectSettings/types';

import { localeCompare } from './sortFunctions';

/**
 * Maps ProjectTasks to TreeItems for TreeView
 * @param tasks - ProjectTasks to map
 * @param disabledIds - IDs of disabled tasks
 * @param viewOnly - Whether to map for view-only TreeView
 * @returns TreeItems[]
 */
export const mapTasksForTreeView = (
  tasks: ProjectTask[],
  disabledIds: string[] = [],
  display:
    | ((task: ProjectTask) => React.ReactNode)
    | React.ReactNode = undefined,
  className:
    | ((task: ProjectTask) => string | undefined)
    | string
    | undefined = undefined
) =>
  tasks.reduce((pv, cv) => {
    pv.push({
      id: cv.taskId,
      name: cv.taskNumberAndName || cv.taskName,
      keywords:
        !cv.taskNumberAndName && cv.taskNumber ? [cv.taskNumber] : undefined,
      children: mapTasksForTreeView(
        cv.childNodes,
        disabledIds,
        display,
        className
      ).sort(sortTree),
      isFolder: cv.isFolder,
      disabled: disabledIds.includes(cv.taskId),
      display:
        typeof display === 'function' ? display(cv) : display || undefined,
      className:
        typeof className === 'function'
          ? className(cv)
          : className || undefined,
    });

    return pv;
  }, [] as TreeItem[]);

/**
 * Maps UserSettingsTasks to TreeItems for TreeView
 * @param tasks - UserSettingsTasks to map
 * @param disabledIds - IDs of disabled tasks
 * @param viewOnly - Whether to map for view-only TreeView
 * @returns TreeItems[]
 */
export const mapUserSettingsTasksForTreeView = (
  tasks: UserSettingsTask[],
  disabledIds: string[] = [],
  display:
    | ((task: UserSettingsTask) => React.ReactNode)
    | React.ReactNode = undefined,
  className:
    | ((task: UserSettingsTask) => string | undefined)
    | string
    | undefined = undefined
) =>
  tasks.reduce((pv, cv) => {
    pv.push({
      id: cv.taskId,
      name: cv.taskName,
      children: cv.childNodes
        ? mapUserSettingsTasksForTreeView(
            cv.childNodes,
            disabledIds,
            display,
            className
          ).sort(sortTree)
        : [],
      isFolder: cv.isFolder,
      disabled: disabledIds.includes(cv.taskId),
      display:
        typeof display === 'function' ? display(cv) : display || undefined,
      className:
        typeof className === 'function'
          ? className(cv)
          : className || undefined,
    });

    return pv;
  }, [] as TreeItem[]);

/**
 * Filters TreeItems for TreeView by folder and task IDs
 * @param tasks - TreeItems to filter
 * @param folderIds - IDs of folders to filter by
 * @param taskIds - IDs of tasks to filter by
 * @returns TreeItems[]
 */
export const filterTasksForTreeView = (
  tasks: TreeItem[],
  folderIds: string[] | null,
  taskIds: string[] | null
) =>
  tasks.reduce((pv, cv) => {
    if (cv.isFolder) {
      if (folderIds?.includes(cv.id)) {
        pv.push(cv);
      } else {
        const filteredChildren = filterTasksForTreeView(
          cv.children,
          folderIds,
          taskIds
        );

        if (filteredChildren.length) {
          pv.push({
            ...cv,
            children: filteredChildren.sort(sortTree),
          });
        }
      }
    } else if (taskIds?.includes(cv.id)) {
      pv.push(cv);
    }

    return pv;
  }, [] as TreeItem[]);

export const mapTemplatesToForTreeView = (
  children: FolderOrItem[]
): TreeItem[] =>
  children.map((child: FolderOrItem) => ({
    id: child.id,
    name: child.name || '',
    isFolder: child.isFolder,
    keywords: child.documentNumber ? [child.documentNumber] : undefined,
    children: child.children ? mapTemplatesToForTreeView(child.children) : [],
    parentNodeId: null,
    isSelected: false,
    display: child.isFolder ? (
      <>
        <TreeViewFolderIcon />
        <OptionDisplay
          title={child.name || ''}
          pretitle={child.documentNumber}
        />
      </>
    ) : (
      <OptionDisplay title={child.name || ''} pretitle={child.documentNumber} />
    ),
    className: child.isFolder ? 'flex items-center gap-2' : undefined,
  }));

export const MAX_TREE_DEPTH = 10;

export const sortByFolderAndName = <
  Item extends { isFolder: boolean } = TreeItem,
>(
  tasks: Item[],
  nameKey: keyof Item,
  optionalKey?: keyof Item
) =>
  tasks
    .sort((a, b) =>
      localeCompare(
        optionalKey
          ? (a[optionalKey] as string) || (a[nameKey] as string)
          : (a[nameKey] as string),
        optionalKey
          ? (b[optionalKey] as string) || (b[nameKey] as string)
          : (b[nameKey] as string)
      )
    )
    .sort((a, b) => {
      if (a.isFolder === b.isFolder) {
        return 0;
      }

      return a.isFolder ? -1 : 1;
    });

export const filterAndSortTreeItems = <Item extends TreeItem = TreeItem>(
  tree: Item[],
  filterString: string = '',
  maxDepth = MAX_TREE_DEPTH,
  depth = 1,
  excludedIds: string[] = []
): Item[] => {
  const lowercasedFilter = filterString ? filterString.toLowerCase() : null;

  return tree.reduce((filtered, item) => {
    if (
      (!lowercasedFilter ||
        item.name.toLowerCase().includes(lowercasedFilter) ||
        item.keywords?.some((keyword) =>
          keyword.toLowerCase().includes(lowercasedFilter)
        )) &&
      !excludedIds.includes(item.id)
    ) {
      if (item.children?.length && depth <= maxDepth) {
        const filteredChildren = sortByFolderAndName(
          filterAndSortTreeItems(
            item.children,
            '',
            maxDepth,
            depth + 1,
            excludedIds
          ),
          'name'
        );

        filtered.push({
          ...item,
          children: filteredChildren,
        });
      } else {
        filtered.push(item);
      }
    } else if (
      item.children &&
      item.children.length &&
      depth <= maxDepth &&
      !excludedIds.includes(item.id)
    ) {
      const filteredChildren = sortByFolderAndName(
        filterAndSortTreeItems(
          item.children,
          filterString,
          maxDepth,
          depth + 1,
          excludedIds
        ),
        'name'
      );

      if (filteredChildren.length) {
        filtered.push({
          ...item,
          children: filteredChildren,
        });
      }
    }

    return sortByFolderAndName(filtered, 'name');
  }, [] as TreeItem<Item>[]);
};

/**
 * Filters, sorts, and collects folder IDs to expand for tree view.
 */
export const filterSortExpandTreeItems = <Item extends TreeItem = TreeItem>(
  tree: Item[],
  filterString: string = '',
  maxDepth = MAX_TREE_DEPTH,
  depth = 1,
  excludedIds: string[] = []
): { items: Item[]; expandedFolderIds: string[] } => {
  // Always filter by excludedIds, even if filterString is empty
  const filteredTree = tree.filter((item) => !excludedIds.includes(item.id));

  if (!filterString) {
    // Just return the filtered tree, sorted, and no expandedFolderIds
    const items = sortByFolderAndName(filteredTree, 'name');
    return { items, expandedFolderIds: [] };
  }

  const lowercasedFilter = filterString.toLowerCase();
  const expandedFolderIdsSet = new Set<string>();

  // Helper: Recursively filter and collect expanded folder ids for all parent folders of matches
  const filterRecursive = (
    nodes: Item[],
    currentDepth: number,
    parentPath: string[]
  ): Item[] =>
    nodes.reduce((filtered: Item[], item) => {
      if (excludedIds.includes(item.id)) {
        return filtered;
      }

      let childrenMatch: Item[] = [];
      const isMatch =
        item.name.toLowerCase().includes(lowercasedFilter) ||
        item.keywords?.some((keyword) =>
          keyword.toLowerCase().includes(lowercasedFilter)
        );

      if (item.children?.length && currentDepth <= maxDepth) {
        if (isMatch) {
          childrenMatch = sortByFolderAndName(
            filterAndSortTreeItems(
              item.children as Item[],
              '',
              maxDepth,
              currentDepth + 1,
              excludedIds
            ),
            'name'
          );
        } else {
          // Pass correct parentPath to children
          childrenMatch = filterRecursive(
            item.children as Item[],
            currentDepth + 1,
            [...parentPath, item.id]
          );
          childrenMatch = sortByFolderAndName(childrenMatch, 'name');
        }
      }

      if (isMatch) {
        // Expand all parent folders in the path
        parentPath.forEach((id) => expandedFolderIdsSet.add(id));
        if (childrenMatch.length) {
          if (item.isFolder) {
            expandedFolderIdsSet.add(item.id);
          }
          filtered.push({
            ...item,
            children: childrenMatch,
          });
        } else {
          filtered.push(item);
        }
      } else if (childrenMatch.length) {
        // If children match, expand all parent folders in the path and this folder
        parentPath.forEach((id) => expandedFolderIdsSet.add(id));
        if (item.isFolder) {
          expandedFolderIdsSet.add(item.id);
        }
        filtered.push({
          ...item,
          children: childrenMatch,
        });
      }

      return sortByFolderAndName(filtered, 'name');
    }, []);

  const items = filterRecursive(filteredTree, depth, []);

  return { items, expandedFolderIds: Array.from(expandedFolderIdsSet) };
};

export const filterSortAndExpandTreeItems = (
  tasksTree: TreeItem[],
  filterString: string = '',
  maxDepth = MAX_TREE_DEPTH,
  depth = 1,
  excludedIds: string[] = []
) => {
  const lowercasedFilter = filterString ? filterString.toLowerCase() : null;

  const expandedItemIds: string[] = [];

  const filteredItems = tasksTree.reduce((filtered, item) => {
    if (
      (!lowercasedFilter ||
        item.name.toLowerCase().includes(lowercasedFilter) ||
        item.keywords?.some((keyword) =>
          keyword.toLowerCase().includes(lowercasedFilter)
        )) &&
      !excludedIds.includes(item.id)
    ) {
      if (item.children?.length && depth <= maxDepth) {
        const filteredChildren = sortByFolderAndName(
          filterAndSortTreeItems(
            item.children,
            '',
            maxDepth,
            depth + 1,
            excludedIds
          ),
          'name'
        );

        filtered.push({
          ...item,
          children: filteredChildren,
        });

        if (lowercasedFilter && filteredChildren.length) {
          expandedItemIds.push(item.id);
        }
      } else {
        filtered.push(item);
      }
    } else if (
      item.children &&
      item.children.length &&
      depth <= maxDepth &&
      !excludedIds.includes(item.id)
    ) {
      const filteredChildren = sortByFolderAndName(
        filterAndSortTreeItems(
          item.children,
          filterString,
          maxDepth,
          depth + 1,
          excludedIds
        ),
        'name'
      );

      if (filteredChildren.length) {
        expandedItemIds.push(item.id);

        filtered.push({
          ...item,
          children: filteredChildren,
        });
      }
    }

    return sortByFolderAndName(filtered, 'name');
  }, [] as TreeItem[]);

  return { filteredItems, expandedItemIds };
};

export const findTreeItemById = <Item extends TreeItem = TreeItem>(
  tree: Item[],
  itemId: string
): Item | null => {
  let result: Item | null = null;

  const search = (items: Item[]) => {
    items.forEach((item) => {
      if (item.id === itemId) {
        result = item;
        return;
      }
      if (item.children?.length > 0) {
        search(item.children as Item[]);
      }
    });
  };

  search(tree);

  return result;
};

export const makeTreeItemBreadcrumbById = (
  tree: TreeItem[],
  itemId: string
) => {
  const breadcrumb: string[] = [];
  const search = (items: TreeItem[], parentNames: string[] = []) =>
    items.reduce((found: boolean, item) => {
      if (found) return true;

      if (item.id === itemId) {
        breadcrumb.push(...parentNames, item.name);
        return true;
      }

      if (item.children?.length > 0) {
        return search(item.children, [...parentNames, item.name]);
      }

      return false;
    }, false);

  search(tree);

  return breadcrumb;
};

/** Returns all child IDs recursively */
export const getChildIds = (item: TreeItem) => {
  const ids: string[] = [];
  const folderIds: string[] = [];

  if (item.children) {
    item.children.forEach((child: TreeItem) => {
      if (child.isFolder) {
        folderIds.push(child.id);

        const { ids: childIds, folderIds: childFolderIds } = getChildIds(child);

        ids.push(...childIds);
        folderIds.push(...childFolderIds);
      } else {
        ids.push(child.id);
      }
    });
  }

  return { ids, folderIds };
};

/** Returns all children recursively */
export const getChildren = <Item extends TreeItem>(item: Item) => {
  const children: Item[] = [];
  const folders: Item[] = [];

  if (item.children) {
    item.children.forEach((child: Item) => {
      if (child.isFolder) {
        folders.push(child);

        const { children: childChildren, folders: childFolders } =
          getChildren(child);

        children.push(...childChildren);
        folders.push(...childFolders);
      } else {
        children.push(child);
      }
    });
  }

  return { children, folders };
};

/** Maps a FolderOrItem to a TreeItem */
export const mapNodeToTreeItem = (
  t: TFunction,
  node: FolderOrItem
): TreeItem => ({
  id: node.id,
  name: node.name || node.title || t('General.NotApplicable'),
  isFolder: node.isFolder,
  children: node.children?.map((child) => mapNodeToTreeItem(t, child)) || [],
});

interface CountChildrenNode {
  isFolder: boolean;
  children: CountChildrenNode[];
}

/** Counts all children of a node */
export const countChildren = <N extends CountChildrenNode = CountChildrenNode>(
  node: N
): number => {
  if (!node.children || node.children.length === 0) {
    return 0;
  }

  let total = 0;
  node.children.forEach((child) => {
    if (child.isFolder) {
      total += countChildren(child);
    } else {
      total += 1;
    }
  });

  return total;
};

/** Returns all parent IDs recursively */
export const findParentIds = (tree: TreeItem[], itemId: string): string[] => {
  const parentIds: string[] = [];

  const search = (items: TreeItem[], currentPath: string[] = []): boolean =>
    items.some((item) => {
      if (item.id === itemId) {
        parentIds.push(...currentPath);
        return true;
      }

      if (item.children?.length > 0) {
        return search(item.children, [...currentPath, item.id]);
      }

      return false;
    });

  search(tree);

  return parentIds;
};
